"use client";

import { useState, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/auth/use-auth";
import { invalidateAfterVoiceAgentChange } from "@/hooks/queries/voice-trial";
import type { VoiceAgentConfig } from "@/lib/voice/agent-config";

export type { VoiceAgentConfig } from "@/lib/voice/agent-config";

export interface VoiceAgent {
  id: string;  // UUID
  tenant_id: string;
  created_by?: string;
  name: string;
  external_id: string;
  system_prompt?: string;
  first_message?: string;
  voice_id?: string;
  languages: string[];
  status: "draft" | "active" | "error" | "disabled";
  last_sync_error?: string;
  config?: VoiceAgentConfig;
  created_at: string;
  updated_at: string;
}

export interface CreateVoiceAgentData {
  name: string;
  system_prompt: string;
  first_message: string;
  voice_id: string;
  languages: string[];
  /** Agent role slugs (ai_use_case_roles); the prompt is prefilled client-side. */
  roles?: string[];
  /** Display name; only meaningful for the custom-only role set. */
  role_label?: string | null;
}

export interface UpdateVoiceAgentData {
  name?: string;
  system_prompt?: string;
  first_message?: string;
  voice_id?: string;
  languages?: string[];
  /** Agent role slugs (ai_use_case_roles); the prompt travels in system_prompt. */
  roles?: string[];
  /** Display name; cleared server-side on a named preset. */
  role_label?: string | null;
}

export function useVoiceAgents() {
  const [agents, setAgents] = useState<VoiceAgent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { fetchWithAuth, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const queryClient = useQueryClient();

  const loadAgents = useCallback(async () => {
    // If auth is still being determined, wait
    if (isAuthLoading) {
      return;
    }

    // If auth is done but user is not authenticated, stop loading
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    setError(null);

    try {
      const response = await fetchWithAuth("/api/agents/voice");

      if (response.ok) {
        const data = await response.json();
        setAgents(data.agents || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to load agents");
      }
    } catch (err) {
      console.error("Error loading voice agents:", err);
      setError("Failed to load agents");
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth, isAuthenticated, isAuthLoading]);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  const getAgent = useCallback(
    (id: string) => {
      return agents.find((agent) => agent.id === id);
    },
    [agents],
  );

  const createAgent = useCallback(
    async (agentData: CreateVoiceAgentData): Promise<VoiceAgent> => {
      const response = await fetchWithAuth("/api/agents/voice", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(agentData),
      });

      if (response.ok) {
        const data = await response.json();
        const newAgent = data.agent;
        setAgents((prev) => [newAgent, ...prev]);
        void invalidateAfterVoiceAgentChange(queryClient);
        return newAgent;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create agent");
      }
    },
    [fetchWithAuth, queryClient],
  );

  const updateAgent = useCallback(
    async (
      id: string,
      agentData: UpdateVoiceAgentData,
    ): Promise<VoiceAgent> => {
      const response = await fetchWithAuth(`/api/agents/voice/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(agentData),
      });

      if (response.ok) {
        const data = await response.json();
        const updatedAgent = data.agent;
        setAgents((prev) =>
          prev.map((agent) => (agent.id === id ? updatedAgent : agent)),
        );
        return updatedAgent;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update agent");
      }
    },
    [fetchWithAuth],
  );

  const setAgentStatus = useCallback(
    async (id: string, status: "active" | "disabled"): Promise<VoiceAgent> => {
      const response = await fetchWithAuth(`/api/agents/voice/${id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to update agent status");

      const agent = data.agent as VoiceAgent;
      setAgents((previous) => previous.map((current) => current.id === id ? agent : current));
      return agent;
    },
    [fetchWithAuth],
  );

  const refreshAgents = useCallback(loadAgents, [loadAgents]);

  return {
    agents,
    isLoading,
    error,
    getAgent,
    createAgent,
    updateAgent,
    setAgentStatus,
    refreshAgents,
  };
}
